<template>
  <form @submit.prevent="addBankAccount" novalidate>
    <div class="drip-h3 font-weight-400 color-noble-blue-500">
      {{ $t('bank.addBank.title') }}
    </div>
    <div class="spacing-32"></div>
    <div class="drip-h2 color-noble-blue-500 font-weight-700">
      {{ $t('bank.addBank.form.beneficiary_name.label') }}
    </div>
    <!-- Beneficiary name -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('bank.addBank.form.beneficiary_name.label') }}
      </div>
      <div class="input-block">
        <input
          id="beneficiary_name"
          name="beneficiary_name"
          data-vv-as="Beneficiary name"
          type="text"
          :placeholder="$t('bank.addBank.form.beneficiary_name.placeholder')"
          v-validate="'required'"
        />
        <span v-if="errors.has('beneficiary_name')" class="error">
          {{ errors.first('beneficiary_name') }}
        </span>
      </div>
    </div>
    <!-- Beneficiary bank name -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('bank.addBank.form.bank_name.label') }}
      </div>
      <div class="input-block">
        <input
          id="bank_name"
          name="bank_name"
          data-vv-as="Bank name"
          type="text"
          :placeholder="$t('bank.addBank.form.bank_name.placeholder')"
          v-validate="'required'"
        />
        <span v-if="errors.has('bank_name')" class="error">
          {{ errors.first('bank_name') }}
        </span>
      </div>
    </div>
    <!-- Beneficiary bank city (optional) -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('bank.addBank.form.bank_city.label') }}
      </div>
      <div class="input-block width-800">
        <input
          id="bank_city"
          name="bank_city"
          type="text"
          data-vv-as="Beneficiary Bank city"
          :placeholder="$t('bank.addBank.form.bank_city.placeholder')"
          v-validate="'required|remove_specials|remove_number'"
        />
        <span v-if="errors.has('bank_city')" class="error">
          {{ errors.first('bank_city') }}
        </span>
      </div>
    </div>
    <!-- Contact name -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('bank.addBank.form.beneficiary_account.label') }}
      </div>
      <div class="input-block width-800">
        <input
          id="beneficiary_account"
          name="beneficiary_account"
          data-vv-as="Beneficiary account number"
          type="text"
          :placeholder="$t('bank.addBank.form.beneficiary_account.placeholder')"
          v-validate="'required|remove_specials'"
        />
        <span v-if="errors.has('beneficiary_account')" class="error">
          {{ errors.first('beneficiary_account') }}
        </span>
      </div>
    </div>
    <!-- Swift Code / ABA routing -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('bank.addBank.form.swift_code.label') }}
      </div>
      <div class="input-block width-800">
        <input
          id="swift_aba_code"
          ref="swift_aba_code"
          name="swift_aba_code"
          data-vv-as="SWIFT Code"
          type="text"
          :placeholder="$t('bank.addBank.form.swift_code.placeholder')"
          v-validate="'required|remove_specials_spaces|swift_code'"
        />
        <span v-if="errors.has('swift_aba_code')" class="error">
          {{ errors.first('swift_aba_code') }}
        </span>
      </div>
    </div>
    <!-- Currency -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('bank.addBank.form.currency.label') }}
      </div>
      <div class="input-block width-800">
        <multiselect
          id="currency"
          name="currency"
          data-vv-as="Currency"
          :placeholder="$t('bank.addBank.form.currency.placeholder')"
          v-model="currencyValue"
          v-validate="'required'"
          class="drip-dropdown"
          :options="options"
          :show-labels="false"
          selectLabel=""
          selectedLabel=""
          deselectLabel=""
          @open="isOpen = true"
          @close="isOpen = false"
        >
          <template slot="option" slot-scope="{ option }">
            {{ option }}
          </template>
          <template slot="caret">
            <img
              v-if="isOpen"
              src="@/assets/images/icons/chevron-down.svg"
              alt="caret"
              class="action-input"
            />
            <img
              v-else
              src="@/assets/images/icons/chevron-down.svg"
              alt="caret"
              class="action-input inverse"
            />
          </template>
        </multiselect>
        <span v-if="errors.has('currency')" class="error">
          {{ errors.first('currency') }}
        </span>
      </div>
    </div>
    <div class="spacing-16"></div>
    <div class="input-container color-noble-blue-500">
      <div class="drip-h3 font-weight-400">
        {{ $t('bank.addBank.form.supporting_document.header') }}
      </div>
      <div class="spacing-2"></div>
      <div class="drip-body">
        <span class="font-weight-400">
          {{ $t('bank.addBank.form.supporting_document.title') }}
        </span>
        <br />
        {{ $t('bank.addBank.form.supporting_document.subtitle') }}
      </div>
      <div class="spacing-12"></div>
      <div class="supporting-document">
        <uploads
          :index="0"
          :title="
            $t('bank.addBank.form.uploads.bank_supporting_document.title')
          "
          :subtext="
            $t('bank.addBank.form.uploads.bank_supporting_document.subtext')
          "
          itemKey="bank_supporting_document"
          docTag="bank_supporting_document"
          :uploadsSaved="uploadsSaved"
          :isOptional="false"
          :documentId="bank && bank.id"
          :uploadBorder="true"
          acceptedFiles=""
          :multiple="['bank_supporting_document']"
          :apiUrl="apiUrl"
        />
        <div class="error" v-if="showErrors">
          {{
            $t(
              'bank.addBank.form.uploads.bank_supporting_document.errorMessage'
            )
          }}
        </div>
      </div>
    </div>
    <div class="spacing-16"></div>
    <div class="drip-h3 color-noble-blue-500 font-weight-400">
      {{ $t('bank.addBank.form.correspondingDetails.title') }}
    </div>
    <div class="spacing-2"></div>
    <div class="drip-body color-noble-blue-500 font-weight-700">
      {{ $t('bank.addBank.form.correspondingDetails.subtext') }}
    </div>
    <div class="spacing-8"></div>
    <!-- Correspondent Bank Name -->
    <div class="input-container">
      <div class="input-label">
        {{ $t('bank.addBank.form.cor_bank_name.label') }}
      </div>
      <div class="input-block width-800">
        <input
          id="cor_bank_name"
          name="cor_bank_name"
          data-vv-as="Correspondent bank name"
          type="text"
          :placeholder="$t('bank.addBank.form.cor_bank_name.placeholder')"
          v-validate="'required|remove_specials|remove_number'"
        />
        <span v-if="errors.has('cor_bank_name')" class="error">
          {{ errors.first('cor_bank_name') }}
        </span>
      </div>
    </div>
    <!-- Correspondent Bank Swift Code-->
    <div class="input-container">
      <div class="input-label">
        {{ $t('bank.addBank.form.cor_bank_swift_code.label') }}
      </div>
      <div class="input-block width-800">
        <input
          id="cor_bank_swift_code"
          name="cor_bank_swift_code"
          data-vv-as="Correspondent bank SWIFT Code"
          type="text"
          :placeholder="$t('bank.addBank.form.cor_bank_swift_code.placeholder')"
          v-validate="'required|remove_specials_spaces|swift_code'"
        />
        <span v-if="errors.has('cor_bank_swift_code')" class="error">
          {{ errors.first('cor_bank_swift_code') }}
        </span>
      </div>
    </div>
    <div class="spacing-16"></div>
    <action-button variant="solid" type="submit">
      <b-icon-arrow-repeat animation="spin" class="mr-2" v-if="submitLoading" />
      {{ buttonText }}
      <img
        src="@/assets/images/common/check-mark.svg"
        class="ml-2"
        v-if="!submitLoading"
      />
    </action-button>
  </form>
</template>

<script>
import ActionButton from '@/components/common/ActionButton'
import Multiselect from 'vue-multiselect'
import { required } from 'vee-validate/dist/rules.esm'
import { CURRENCY_MAP } from '@/utils/constants'
import Uploads from '@/components/common/Uploads'
import { uploadFilesBus } from '../../main'

export default {
  props: ['submitLoading', 'bank', 'buttonText', 'uploadsSaved'],
  components: {
    ActionButton,
    Uploads,
    Multiselect
  },
  data() {
    return {
      currencyValue: '',
      formData: {},
      options: Object.keys(CURRENCY_MAP),
      apiUrl: `${process.env.VUE_APP_DRIP_API_HOST}/v1/onboarding/leads/${this.$store.getters.accountId}/documents`,
      isOpen: false
    }
  },
  created() {
    this.$validator.extend('swift_code', {
      ...required,
      validate(value) {
        if (value && (value.length < 8 || value.length > 11)) {
          return false
        }
        return true
      },
      getMessage: () => this.$t('bank.addBank.form.swift_code.errorMessage')
    })
    this.$validator.extend('remove_specials', {
      ...required,
      validate(value) {
        return /^[a-zA-Z0-9\s]*$/.test(value)
      }
    })
    this.$validator.extend('remove_specials_spaces', {
      ...required,
      validate(value) {
        return /^[a-zA-Z0-9]*$/.test(value)
      }
    })
    this.$validator.extend('remove_number', {
      ...required,
      validate(value) {
        return !/[0-9]/.test(value)
      }
    })
    this.$validator.extend('numbers_only', {
      ...required,
      validate(value) {
        return /^[0-9]*$/.test(value)
      }
    })
  },
  mounted() {
    if (this.bank && this.bank.id) {
      this.prefillForm()
    }
  },
  computed: {
    showErrors() {
      return this.uploadsSaved[0] && this.uploadsSaved[0].errorMessage
    }
  },
  methods: {
    setFormValue(field, value) {
      if (field) {
        document.getElementById(field).setAttribute('value', value || '')
      }
    },
    prefillForm() {
      if (this.bank.id) {
        this.currencyValue =
          Object.keys(CURRENCY_MAP).find(
            (key) => CURRENCY_MAP[key] === this.bank.currency
          ) || ''
        this.setFormValue('beneficiary_name', this.bank.beneficiary_name)
        this.setFormValue('bank_name', this.bank.bank_name)
        this.setFormValue('bank_city', this.bank.bank_city)
        this.setFormValue('beneficiary_account', this.bank.account_number)
        this.setFormValue('swift_aba_code', this.bank.swift_code)
        this.setFormValue('cor_bank_name', this.bank.correspondent_bank_name)
        this.setFormValue(
          'cor_bank_swift_code',
          this.bank.correspondent_swift_code
        )
      }
    },
    openListOfBank() {
      this.$router.push({ name: 'bankAccounts' })
    },
    scrollToErrors() {
      if (document.querySelector('.error')) {
        document.querySelector('.error').scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
      return document.querySelector('.error')
    },
    addBankAccount(e) {
      this.$validator.validate()
      if (
        this.uploadsSaved[0] &&
        !this.uploadsSaved[0].bank_supporting_document.length
      ) {
        uploadFilesBus.$emit('documentRequired', true)
        this.scrollToErrors()
        return false
      }
      uploadFilesBus.$emit('documentRequired', false)
      this.$validator
        .validate()
        .then((result) => {
          if (result) {
            const form = e.target.elements
            this.formData = {
              vars: {
                bank_details: {
                  account_number: form.beneficiary_account.value,
                  currency: CURRENCY_MAP[this.currencyValue],
                  client_name: form.beneficiary_name.value,
                  address: form.bank_city.value,
                  ifsc: null,
                  swift_code: form.swift_aba_code.value
                    .toString()
                    .toUpperCase(),
                  correspondent_bank: form.cor_bank_name.value,
                  correspondent_swift_code: form.cor_bank_swift_code.value
                    .toString()
                    .toUpperCase(),
                  correspondent_fedwire: null,
                  beneficiary_name: form.beneficiary_name.value,
                  bank_attributes: {
                    name: form.bank_name.value
                  }
                },
                supporting_documents: [
                  ...this.uploadsSaved[0].bank_supporting_document.map((x) => {
                    return { id: x.docId }
                  })
                ]
              }
            }
            this.formData.vars.bank_details.id =
              this.$route.query.bid || this.$route.params.bid || ''
            this.$emit('addBankAccount', this.formData)
          } else {
            this.scrollToErrors()
          }
        })
        .catch((e) => {
          this.scrollToErrors()
          console.log(e)
        })
    }
  }
}
</script>
<style lang="scss">
#currency::placeholder {
  color: $neutral-gray-500;
}
</style>
<style lang="scss" scoped>
.input-container {
  width: 100%;
  margin-bottom: 16px;
  .input-label {
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: left;
    color: $noble-blue-500;
    margin-bottom: 8px;
  }
  .input-block {
    display: flex;
    flex-direction: column;
    .drip-dropdown {
      height: 56px;
      width: 400px;
      @media screen and (max-width: 768px) {
        width: 100%;
      }
      .action-input {
        position: absolute;
        cursor: pointer;
        right: 0;
        padding-left: 1rem;
        padding-right: 1rem;
        line-height: 2.6rem;
        font-size: 1.2rem;
        border-bottom-right-radius: 5px;
        border-top-right-radius: 5px;
        top: 1.6rem;
        &.inverse {
          transform: rotate(180deg);
        }
      }
      ::v-deep .multiselect__select {
        height: 100%;
        width: 20px;
        margin-right: 8px;
        padding: 0px;
        &::before {
          z-index: 1;
        }
      }
      ::v-deep .multiselect__tags {
        outline: 0px;
        height: 56px;
        padding: 16px;
        padding-right: 36px;
        border-radius: 8px;
        border: 1px solid $noble-blue-500;
        color: $noble-blue-500;
        margin-bottom: 4px;
        .multiselect__placeholder {
          color: $neutral-gray-500;
          margin-top: -2px;
          padding-top: 0px;
          padding-left: 5px;
          font-size: 16px;
        }
        .multiselect__single {
          color: $noble-blue-500;
          font-weight: 700;
          vertical-align: bottom;
          width: 90% !important;
        }
      }
      ::v-deep .multiselect__option {
        background-color: white;
        color: $noble-blue-500;
      }
      ::v-deep .multiselect__option--highlight {
        background-color: white;
        color: $noble-blue-500;
      }
      ::v-deep .multiselect__option--selected {
        background-color: white;
      }
      ::v-deep .multiselect__option:hover {
        background-color: $sky-blue-50;
        color: $wealthy-green-600;
      }
      ::v-deep .multiselect__content-wrapper {
        border: 1px solid $noble-blue-50;
        box-shadow: 0px 6px 12px 0px #0a2e5733;
      }
    }
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
    }
    input {
      outline: 0px;
      width: 400px;
      height: 56px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid $noble-blue-500;
      color: $noble-blue-500;
      font-size: 16px;
      font-weight: 700;
      &::placeholder {
        font-weight: 400;
        color: $neutral-gray-500;
      }
      &.width-800 {
        width: 808px;
      }
      @media screen and (max-width: 768px) {
        width: 100%;
      }
    }
    .error {
      color: $red-400;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: left;
      margin-top: 8px;
      @media screen and (max-width: 768px) {
        margin-top: 2px;
      }
    }
  }
  .supporting-document {
    display: flex;
    flex-direction: column;
    max-width: 800px;
    width: 100%;
    .error {
      color: $red-400;
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: left;
      margin-top: 8px;
      @media screen and (max-width: 768px) {
        margin-top: 2px;
      }
    }
  }
}
</style>
